/**
 * Compiler — Core 2 of the NSPP runtime.
 *
 * Packs the scene into a single Float32Array of per-instance attributes.
 * Layout (per instance = 15 vec4s = 60 floats = 240 bytes):
 *
 *  attr 0:  vec4 posSize       (x, y, w, h)                       bytes 0..15
 *  attr 1:  vec4 fillRGBA      (r, g, b, a)                       bytes 16..31
 *  attr 2:  vec4 stroke        (width, r, g, b)                   bytes 32..47
 *  attr 3:  vec4 shadow        (offX, offY, blur, spread)         bytes 48..63
 *  attr 4:  vec4 shadowColor   (r, g, b, a)                       bytes 64..79
 *  attr 5:  vec4 xform         (cosθ, sinθ, scaleX, scaleY)       bytes 80..95
 *  attr 6:  vec4 prev          (prevX, prevY, prevCosθ, prevSinθ) bytes 96..111
 *  attr 7:  vec4 misc          (radius, opacity, z, sdfKind)      bytes 112..127
 *           └─ sdfKind: 0..15 in the .w slot, scaled to [0,1] by
 *              multiplying by (1/15) before write and reading back as
 *              `vSdfKind = vMisc.w * 15.0` in the shader. Stroke alpha
 *              used to live here but SDF shapes don't need it, and the
 *              rest of the engine reads aStroke.w for stroke coverage.
 *  attr 8:  vec4 halfSize      (halfW, halfH, _, _)               bytes 128..143
 *  attr 9:  vec4 radii         (TL, TR, BR, BL)                   bytes 144..159
 *  attr 10: vec4 filterA       (blur, brightness, contrast, saturate)  bytes 160..175
 *  attr 11: vec4 filterB       (hueRotate, invert, _, _)          bytes 176..191
 *  attr 12: vec4 gradient      (endR, endG, endB, angle)          bytes 192..207
 *  attr 13: vec4 sdfParamsA    (p0, p1, p2, p3)                   bytes 208..223
 *  attr 14: vec4 sdfParamsB    (p4, p5, p6, p7)                   bytes 224..239
 *
 * 240 bytes/instance.
 *
 * Why 15 vec4s and not 16: WebGL2 caps MAX_VERTEX_ATTRIB_STRIDE at 255
 * bytes on most implementations (SwiftShader enforces this strictly).
 * 16 × 16 = 256, which fails the cap. 15 × 16 = 240 fits.
 */

import type { Scene, SceneNode } from "./sceneGraph";
import { sortedNodes } from "./sceneGraph";

export const FLOATS_PER_INSTANCE = 60;
export const BYTES_PER_INSTANCE = FLOATS_PER_INSTANCE * 4;

const ATTR_POS_SIZE = 0;
const ATTR_FILL = 1;
const ATTR_STROKE = 2;
const ATTR_SHADOW = 3;
const ATTR_SHADOW_COLOR = 4;
const ATTR_XFORM = 5;
const ATTR_PREV = 6;
const ATTR_MISC = 7;
const ATTR_HALF_SIZE = 8;
const ATTR_RADII = 9;
const ATTR_FILTER_A = 10;
const ATTR_FILTER_B = 11;
const ATTR_GRADIENT = 12;
const ATTR_SDF_PARAMS_A = 13;
const ATTR_SDF_PARAMS_B = 14;

/** Integer ids matching the order of `SdfKind` strings the rect pipeline
 *  dispatches on. Keep in sync with src/sdfParametric.ts. */
const SDF_KIND_IDS: Record<string, number> = {
  rect: 0,
  circle: 1,
  roundedBox: 2,
  ring: 3,
  capsule: 4,
  polygon: 5,
  star: 6,
  heart: 7,
  arc: 8,
  segment: 9,
  bezier: 10,
  rhombus: 11,
  trapezoid: 12,
  pie: 13,
  horseshoe: 14,
  orientedBox: 15,
  equilateralTri: 16,
  vesica: 17,
  moon: 18,
  ellipse: 19,
  cross: 20,
  parallelogram: 21,
  hexagram: 22,
  bobblyCross: 23,
  egg: 24,
  parabolaSeg: 25,
  stairs: 26,
  circleWave: 27,
  hyperbola: 28,
  coolS: 29,
  pentagram: 30,
  regularStar: 31,
  isoscelesTri: 32,
  roundedX: 33,
  circleCross: 34,
  orientedVesica: 36,  // skip 35 (sdTriangleIQ — not yet wired)
  parabola: 37,
  quadraticCircle: 38,
  squircle: 39,
  gear: 40,
  spike: 41,
  plus: 42,
  cloud: 43,
  lightning: 44,
  speechBubble: 45,
  heartBroken: 46,
  dumbbell: 47,
  flower: 48,
  shield: 49,
  bookmark: 50,
  arrow: 51,
  cog: 52,
  tapeStrip: 53,
  asterisk: 54,
  spiral: 55,
};

export interface CompiledScene {
  /** Packed per-instance attribute data, length = nodeCount * FLOATS_PER_INSTANCE. */
  data: Float32Array;
  /** Number of instances (== number of nodes). */
  count: number;
  /** Monotonic counter, used by the engine to skip no-op uploads. */
  version: number;
}

export function compile(scene: Scene): CompiledScene {
  const nodes = sortedNodes(scene);
  const n = nodes.length;
  const data = new Float32Array(n * FLOATS_PER_INSTANCE);

  for (let i = 0; i < n; i++) {
    const node = nodes[i];
    if (!node) continue;
    packNode(data, i, node);
  }

  return { data, count: n, version: scene.version };
}

function packNode(out: Float32Array, i: number, n: SceneNode): void {
  const base = i * FLOATS_PER_INSTANCE;

  // attr 0: posSize
  out[base + 0] = n.x;
  out[base + 1] = n.y;
  out[base + 2] = n.w;
  out[base + 3] = n.h;

  // attr 1: fill RGBA
  out[base + 4] = n.fill.r;
  out[base + 5] = n.fill.g;
  out[base + 6] = n.fill.b;
  out[base + 7] = n.fill.a;

  // attr 2: stroke width + rgba
  out[base + 8] = n.strokeWidth;
  out[base + 9] = n.stroke.r;
  out[base + 10] = n.stroke.g;
  out[base + 11] = n.stroke.b;

  // attr 3: shadow offsets/blur/spread
  out[base + 12] = n.shadowOffsetX;
  out[base + 13] = n.shadowOffsetY;
  out[base + 14] = n.shadowBlur;
  out[base + 15] = n.shadowSpread;

  // attr 4: shadow rgba
  out[base + 16] = n.shadowColor.r;
  out[base + 17] = n.shadowColor.g;
  out[base + 18] = n.shadowColor.b;
  out[base + 19] = n.shadowColor.a;

  // attr 5: transform
  const cos = Math.cos(n.rotation);
  const sin = Math.sin(n.rotation);
  out[base + 20] = cos;
  out[base + 21] = sin;
  out[base + 22] = n.scaleX;
  out[base + 23] = n.scaleY;

  // attr 6: prev transform (for velocity blur)
  out[base + 24] = n.prevX;
  out[base + 25] = n.prevY;
  out[base + 26] = Math.cos(n.prevRotation);
  out[base + 27] = Math.sin(n.prevRotation);

  // attr 7: misc (radius, opacity, z, sdfKind). The sdfKind lives in the
  // .w slot — written as id/15.0 so it decodes losslessly to a 0..15 int
  // in the vertex shader (vSdfKind = vMisc.w * 15.0 + 0.5). Replaces the
  // old `strokeA` slot which is now in aStroke.w (which the existing
  // shader path already uses for stroke coverage).
  const r = n.radii;
  const avgRadius = r ? (r[0] + r[1] + r[2] + r[3]) * 0.25 : 0;
  const kind = n.sdfKind;
  const kindId = kind ? (SDF_KIND_IDS[kind] ?? 0) : 0;
  out[base + 28] = avgRadius;
  out[base + 29] = n.opacity;
  out[base + 30] = n.zIndex;
  out[base + 31] = kindId / 15; // decoded in shader as vMisc.w * 15.0

  // attr 8: halfSize (precomputed)
  out[base + 32] = n.w * 0.5;
  out[base + 33] = n.h * 0.5;
  out[base + 34] = 0;
  out[base + 35] = 0;

  // attr 9: per-corner radii [TL, TR, BR, BL]
  out[base + 36] = n.radii[0] ?? 0;
  out[base + 37] = n.radii[1] ?? 0;
  out[base + 38] = n.radii[2] ?? 0;
  out[base + 39] = n.radii[3] ?? 0;

  // attr 10: filterA (blur, brightness, contrast, saturate)
  const f = n.filter;
  out[base + 40] = f?.blur ?? 0;
  out[base + 41] = f?.brightness ?? 1;
  out[base + 42] = f?.contrast ?? 1;
  out[base + 43] = f?.saturate ?? 1;

  // attr 11: filterB (hueRotate, invert, _, _)
  out[base + 44] = f?.hueRotate ?? 0;
  out[base + 45] = f?.invert ?? 0;
  out[base + 46] = 0;
  out[base + 47] = 0;

  // attr 12: gradient end color + angle (radians). a = mix(0,1) flag — the
  // shader reads aGradient.w > 0.5 to enable gradient interpolation.
  if (n.gradientEnd) {
    out[base + 48] = n.gradientEnd.r;
    out[base + 49] = n.gradientEnd.g;
    out[base + 50] = n.gradientEnd.b;
    out[base + 51] = n.gradientAngle ?? 0;
  } else {
    out[base + 48] = n.fill.r;
    out[base + 49] = n.fill.g;
    out[base + 50] = n.fill.b;
    out[base + 51] = 0; // 0 = no gradient
  }

  // attr 13: first 4 SDF params (meaning depends on the shape).
  const pa = n.sdfParamsA ?? [0, 0, 0, 0];
  out[base + 52] = pa[0];
  out[base + 53] = pa[1];
  out[base + 54] = pa[2];
  out[base + 55] = pa[3];

  // attr 14: optional 2nd 4 SDF params for shapes with >4 parameters.
  const pb = n.sdfParamsB ?? [0, 0, 0, 0];
  out[base + 56] = pb[0];
  out[base + 57] = pb[1];
  out[base + 58] = pb[2];
  out[base + 59] = pb[3];
}

export const ATTR_LOCATIONS = {
  posSize: ATTR_POS_SIZE,
  fill: ATTR_FILL,
  stroke: ATTR_STROKE,
  shadow: ATTR_SHADOW,
  shadowColor: ATTR_SHADOW_COLOR,
  xform: ATTR_XFORM,
  prev: ATTR_PREV,
  misc: ATTR_MISC,
  halfSize: ATTR_HALF_SIZE,
  radii: ATTR_RADII,
  filterA: ATTR_FILTER_A,
  filterB: ATTR_FILTER_B,
  gradient: ATTR_GRADIENT,
  sdfParamsA: ATTR_SDF_PARAMS_A,
  sdfParamsB: ATTR_SDF_PARAMS_B,
} as const;